// ─────────────────────────────────────────────────────────────────────────────
// Orion Studio — les 23 vues V4 portées VERBATIM.
//
// Les routes perdent leur préfixe, comme prévu au portage :
//   /v4/portfolio → /portfolio
// Rien d'autre ne change : mêmes noms de route (`v4-portfolio`…), mêmes
// composants, mêmes paramètres. Les `router.push({ name: 'v4-channel' })` qui
// parsèment les vues continuent donc de fonctionner sans retouche.
// ─────────────────────────────────────────────────────────────────────────────
import { createApp, defineAsyncComponent } from 'vue';
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import App from './App.vue';
import { useSession } from './composables/session';
import { loadCapabilities } from './composables/capabilities.composable';
import { accueilDuCompte } from './helper/accueil.helper';
import { i18n } from './i18n';
import './style.css';

/**
 * Les écrans, sur leur chemin CANONIQUE.
 *
 * L'ancien préfixe `/v4` est ajouté juste après en ALIAS — voir
 * `withLegacyV4Alias` — pour que les liens d'hier mènent encore quelque part.
 */
// Explorer et le Sonar d'un format : une seule page, et une seule instance pour
// toutes leurs adresses (passer de la liste à une étude ne la remonte pas).
const Sonar = defineAsyncComponent(() => import('./pages/epure/ESonar.vue'));

const screens: RouteRecordRaw[] = [
  { path: '/login', name: 'login', component: () => import('./views/LoginView.vue') },
  // L'invitation à rejoindre une organisation (1er octobre 2026) : ouverte
  // connecté ou non, l'invité n'a souvent pas encore de compte.
  { path: '/invitation/:jeton', name: 'invitation', component: () => import('./views/InvitationView.vue') },
  // L'équipe de l'organisation, pour ses administrateurs.
  { path: '/equipe', name: 'v4-equipe', component: () => import('./pages/v4/V4Equipe.vue') },
  // Toutes les conversations, pour les relire ou les reprendre (2 octobre 2026).
  { path: '/conversations', name: 'v4-conversations', component: () => import('./pages/v4/V4Conversations.vue') },
  // Les Réglages (9 octobre 2026) : le nom, la langue, et l'équipe pour qui l'administre ; ouverts à tous.
  { path: '/compte', name: 'v4-compte', component: () => import('./pages/epure/EReglages.vue') },
  { path: '/reglages', redirect: '/compte' },

  // ── Le DOSSIER — la porte d'entrée ────────────────────────────────────────
  //
  // Sans module dans la table de gardes plus bas, et c'est délibéré : un compte
  // qui n'aurait aucun module accordé doit quand même pouvoir voir ses
  // dossiers, sinon il tombe sur un écran vide sans savoir si c'est un droit
  // qui manque ou s'il n'a rien.
  { path: '/dossiers', name: 'v4-projets', component: () => import('./pages/v4/V4Projets.vue') },

  // ── Les modules du DOSSIER ────────────────────────────────────────────────
  //
  // Décision du CEO, 24 septembre 2026 : la marque se MESURE (chaîne, audit,
  // config), le dossier se PILOTE. Le Bureau d'études, les ISOs, le calendrier
  // et la carte cosmique regardent toutes les marques du dossier à la fois.
  // Les anciens chemins par marque restent servis : des liens existent.
  { path: '/dossier/bureau', name: 'v4-dossier-bureau', component: () => import('./pages/v4/V4Decisions.vue') },
  // PreCog du dossier (24 septembre 2026) : la même page que `/precog`, qui
  // reste l'écran d'administration. Le NOM de route décide de la portée.
  { path: '/dossier/precog', name: 'v4-dossier-precog', component: () => import('./pages/v4/V4Precog.vue') },
  { path: '/dossier/isos', name: 'v4-dossier-isos', component: () => import('./pages/v4/V4DossierIsos.vue') },
  { path: '/dossier/calendrier', name: 'v4-dossier-calendar', component: () => import('./pages/v4/V4StudioCalendar.vue') },
  // ORION BETA (25 septembre 2026) : Sentinel, la carte des territoires qu'un
  // admin a ouverts au compte, par couches. Au même niveau que le dossier
  // depuis le soir même : son ancien chemin sous le dossier redirige.
  { path: '/sentinel', name: 'v4-dossier-sentinel', component: () => import('./pages/v4/V4Sentinel.vue') },
  { path: '/dossier/sentinel', redirect: '/sentinel' },
  // Discovery : les insights que ce terrain ouvre, avec ou sans dossier.
  { path: '/discovery', name: 'v4-discovery', component: () => import('./pages/v4/V4Discovery.vue') },
  // Replica : éprouver une idée, sur une population choisie ou l'audience d'un dossier.
  { path: '/replica', name: 'v4-replica', component: () => import('./pages/v4/V4Replica.vue') },
  // Le RADAR (28 septembre 2026) : les territoires du compte en anneaux autour
  // de leur pays, relus chaque matin, et ce qui monte chez eux.
  // L'interface épurée (9 octobre 2026) est la seule depuis le 12 octobre 2026 :
  // la classique est archivée sous le tag git `interface-classique`.
  { path: '/radar', name: 'v4-radar', component: () => import('./pages/epure/ERadar.vue') },
  // Le catalogue des territoires (12 octobre 2026).
  { path: '/catalogue', name: 'v4-catalogue', component: () => import('./pages/epure/ECatalogue.vue') },

  // ── ENTERTAINMENT (26 septembre 2026) : le premier vertical ───────────────
  // Les formats et leur escalier de preuves ; un format, un test ; les
  // soirées, leurs enveloppes scellées et le registre ; les diffuseurs ; le
  // brief du lundi ; et les livrables, en pages à imprimer.
  // Zeus, l'assistant de Projector (2 octobre 2026).
  // Zeus, l'assistant de tout Orion (3 octobre 2026) : né dans Projector, son ancienne adresse y mène.
  { path: '/zeus', name: 'v4-zeus', component: () => import('./pages/v4/V4Zeus.vue') },
  // La simulation, version allégée (3 novembre 2026) : les villes ouvertes au compte, de 4 h à maintenant.
  { path: '/simulation', name: 'v4-simulation', component: () => import('./pages/epure/ESimulation.vue') },
  // Les tâches récurrentes de Zeus (26 octobre 2026).
  // Dans Zeus depuis le 3 novembre 2026 (onglet Tâches) : les anciennes adresses y mènent.
  { path: '/taches', redirect: { path: '/zeus', query: { vue: 'taches' } } },
  { path: '/taches/:id', redirect: (to) => ({ path: '/zeus', query: { tache: String(to.params.id) } }) },
  { path: '/entertainment/zeus', redirect: (to) => ({ path: '/zeus', query: to.query }) },
  { path: '/entertainment', name: 'v4-ent-formats', component: () => import('./pages/epure/EFormats.vue') },
  { path: '/entertainment/format/:formatId', name: 'v4-ent-format', component: () => import('./pages/epure/EFormat.vue') },
  // Les documents et l'écoute d'un format (10 octobre 2026) : sortis des onglets de la Fiche pour la barre, comme le Sonar.
  { path: '/entertainment/format/:formatId/documents', name: 'v4-ent-format-documents', component: () => import('./pages/epure/EFormatSection.vue') },
  { path: '/entertainment/format/:formatId/ecoute', name: 'v4-ent-format-ecoute', component: () => import('./pages/epure/EFormatSection.vue') },
  // Le suivi d'un format à l'antenne (19 octobre 2026) : ses rendez-vous, leurs passages, leurs audiences.
  { path: '/entertainment/format/:formatId/suivi', name: 'v4-ent-format-suivi', component: () => import('./pages/epure/EFormatSection.vue') },
  // Une marche de l'escalier, tous ses tests (13 octobre 2026).
  { path: '/entertainment/format/:formatId/marche/:marche', name: 'v4-ent-format-marche', component: () => import('./pages/epure/EMarche.vue') },
  // Le Sonar du DOSSIER (9 octobre 2026) : les questions sur ce format, sans menu
  // déroulant. Le Sonar global de Projector s'appelle désormais Explorer.
  { path: '/entertainment/format/:formatId/sonar', name: 'v4-ent-format-sonar', component: Sonar },
  { path: '/entertainment/format/:formatId/sonar/:sonarId', name: 'v4-ent-format-sonar-etude', component: Sonar },
  { path: '/entertainment/test/:testId', name: 'v4-ent-test', component: () => import('./pages/epure/ETest.vue') },
  { path: '/entertainment/soirees', name: 'v4-ent-soirees', component: () => import('./pages/epure/ESoirees.vue') },
  { path: '/entertainment/enveloppe/:enveloppeId', name: 'v4-ent-enveloppe', component: () => import('./pages/v4/V4EntSoirees.vue') },
  { path: '/entertainment/diffuseurs', name: 'v4-ent-diffuseurs', component: () => import('./pages/epure/EDiffuseurs.vue') },
  { path: '/entertainment/diffuseurs/:diffuseurId', name: 'v4-ent-diffuseur', component: () => import('./pages/v4/V4EntDiffuseurs.vue') },
  { path: '/entertainment/brief', name: 'v4-ent-brief', component: () => import('./pages/epure/EBrief.vue') },
  { path: '/entertainment/sonar', name: 'v4-ent-sonar', component: Sonar },
  { path: '/entertainment/sonar/:sonarId', name: 'v4-ent-sonar-etude', component: Sonar },
  { path: '/entertainment/pays', name: 'v4-ent-pays', component: () => import('./pages/epure/EExport.vue') },
  { path: '/entertainment/pays/:paysId', name: 'v4-ent-pays-detail', component: () => import('./pages/v4/V4EntPays.vue') },
  { path: '/entertainment/livrable/:genre/:id', name: 'v4-ent-livrable', component: () => import('./pages/v4/V4EntLivrable.vue') },

  // ── POLIS (3 octobre 2026) : le module politique, interne ────────────────
  // Un scrutin, ses vagues et leur projection, son électorat, son registre.
  // Polis épuré (10 octobre 2026) : l'élection se choisit dans la barre, comme le dossier de Projector.
  // L'annuaire des personnalités et leur suivi (15 octobre 2026), hors de toute élection : avant les onglets de l'élection.
  { path: '/polis/personnalites', name: 'v4-polis-personnalites', component: () => import('./pages/epure/EPolisPersonnalites.vue') },
  // La carte des personnalités (16 octobre 2026) : un système solaire par mouvement. Avant la fiche : « carte » n'est pas un identifiant.
  { path: '/polis/personnalites/carte', name: 'v4-polis-carte', component: () => import('./pages/epure/EPolisCarte.vue') },
  { path: '/polis/personnalites/:id/:onglet?', name: 'v4-polis-personnalite', component: () => import('./pages/epure/EPolisPersonnalite.vue') },
  { path: '/polis/suivis', name: 'v4-polis-suivis', component: () => import('./pages/epure/EPolisSuivis.vue') },
  { path: '/polis/:onglet?', name: 'v4-polis', component: () => import('./pages/epure/EPolis.vue') },

  // ── SONAR seul (2 octobre 2026) : n'importe quelle question, sans format ──
  { path: '/sonar', name: 'v4-sonar', component: Sonar },
  { path: '/sonar/:sonarId', name: 'v4-sonar-etude', component: Sonar },
  { path: '/sonar/livrable/:genre/:id', name: 'v4-sonar-livrable', component: () => import('./pages/v4/V4EntLivrable.vue') },

  // ── Général — les écrans cross-marques ────────────────────────────────────
  { path: '/portfolio', name: 'v4-portfolio', component: () => import('./pages/v4/V4Portfolio.vue') },
  { path: '/audits', name: 'v4-audits', component: () => import('./pages/v4/V4Audits.vue') },
  { path: '/blast', name: 'v4-blast', component: () => import('./pages/v4/V4Blast.vue') },
  { path: '/map', name: 'v4-cosmic-map', component: () => import('./pages/v4/V4CosmicMap.vue') },
  { path: '/monday-brief', name: 'v4-monday-brief', component: () => import('./pages/v4/V4MondayBrief.vue') },
  { path: '/decisions/:decisionId', name: 'v4-decision-dossier', component: () => import('./pages/v4/V4DecisionDossier.vue') },

  // ── Marque — les écrans scopés ────────────────────────────────────────────
  { path: '/brands/:id', name: 'v4-channel', component: () => import('./pages/v4/V4Channel.vue') },
  { path: '/brands/:id/deep-dive/:dimension', name: 'v4-deep-dive', component: () => import('./pages/v4/V4DeepDive.vue') },
  { path: '/brands/:id/decisions', name: 'v4-decisions', component: () => import('./pages/v4/V4Decisions.vue') },
  { path: '/brands/:id/brief', name: 'v4-brief', component: () => import('./pages/v4/V4Brief.vue') },
  { path: '/brands/:id/program', name: 'v4-program', component: () => import('./pages/v4/V4Program.vue') },
  { path: '/brands/:id/report', name: 'v4-audit-report', component: () => import('./pages/v4/V4AuditReport.vue') },
  // Les ISOs sont au DOSSIER depuis le 24 septembre 2026 : l'ancien chemin par
  // marque ouvre la même page, sur le dossier de cette marque.
  { path: '/brands/:id/arena', name: 'v4-brand-arena', component: () => import('./pages/v4/V4DossierIsos.vue') },
  { path: '/brands/:id/territories', name: 'v4-brand-territories', component: () => import('./pages/v4/V4BrandTerritories.vue') },
  { path: '/brands/:id/presence-llm', name: 'v4-presence-llm', component: () => import('./pages/v4/V4PresenceLlm.vue') },

  // ── Studio — l'atelier cross-marques ──────────────────────────────────────
  { path: '/studio/new', name: 'v4-studio-wizard', component: () => import('./pages/v4/V4StudioWizard.vue') },
  { path: '/studio/config', name: 'v4-studio-config', component: () => import('./pages/v4/V4StudioConfig.vue') },
  { path: '/studio/calendar', name: 'v4-studio-calendar', component: () => import('./pages/v4/V4StudioCalendar.vue') },
  { path: '/studio/personas', name: 'v4-studio-personas', component: () => import('./pages/v4/V4StudioPersonas.vue') },
  { path: '/studio/authors', name: 'v4-studio-authors', component: () => import('./pages/v4/V4StudioAuthors.vue') },
  { path: '/studio/hubs', name: 'v4-studio-hubs', component: () => import('./pages/v4/V4StudioHubs.vue') },
  { path: '/studio/drifts', name: 'v4-studio-drifts', component: () => import('./pages/v4/V4StudioDrifts.vue') },
];

/**
 * L'administration BAS NIVEAU, sous `/admin`.
 *
 * ── Pourquoi ces deux ecrans ne sont pas du Studio ─────────────────────────
 *
 * Le Studio est l'atelier d'une marque : on y cree, on y calibre, on y publie.
 * Ces deux ecrans-la ne configurent pas une marque — ils configurent le MOTEUR
 * que toutes les marques partagent : le catalogue des territoires, et les
 * ponderations qui entrent dans chaque score.
 *
 * Le rangement dit donc la portee. Un ecran qui modifie le referentiel de tout
 * le monde ne doit pas voisiner avec « Nouvelle marque » : la proximite fait
 * croire a une portee locale.
 *
 * C'est la meme frontiere que `orion-admin` marque d'un drapeau
 * `is_global_config` sur ces deux modules exactement.
 *
 * Les anciens chemins `/studio/...` restent servis en ALIAS : des liens
 * existent, dans des conversations et des signets.
 */
const adminScreens: RouteRecordRaw[] = [
  // ── Partis dans Orion Admin (8 octobre 2026) ──────────────────────────────
  //
  // La gestion des territoires, la station et les pondérations vivent dans la
  // console d'administration, et le studio n'en montre plus rien. Leurs
  // anciennes adresses (signets, liens dans une conversation) mènent à
  // l'accueil du compte, sans un mot sur la console.
  ...[
    '/admin/territories', '/studio/territories', '/v4/studio/territories', '/v4/admin/territories',
    '/admin/carte', '/admin/station', '/v4/admin/carte',
    '/admin/weights', '/studio/weights', '/v4/studio/weights', '/v4/admin/weights',
  ].map((path): RouteRecordRaw => ({ path, redirect: () => accueilDuCompte() })),
  {
    path: '/admin/calibration',
    name: 'v4-calibration',
    alias: ['/calibration', '/v4/admin/calibration'],
    component: () => import('./pages/v4/V4Calibration.vue'),
  },
  {
    path: '/precog',
    name: 'v4-precog',
    alias: ['/v4/precog'],
    component: () => import('./pages/v4/V4Precog.vue'),
  },
];

/**
 * `/v4/…` reste servi, mais n'est plus la forme canonique.
 *
 * ── Pourquoi le préfixe existait, et pourquoi il n'a plus de sens ──────────
 *
 * Il venait du front d'origine, où les écrans V4 cohabitaient avec ceux des
 * versions précédentes : le préfixe était ce qui les distinguait. La V4 est
 * désormais la SEULE version d'Orion — il ne distingue plus rien, et une URL
 * qui annonce un numéro de version promet une V5 qui n'arrivera pas.
 *
 * Les liens du produit ont été réécrits : 79 chemins dans 18 fichiers. La
 * réécriture a été restreinte aux racines de route connues, parce que les
 * chemins d'import (`@/components/v4/V4Layout.vue`) contiennent la même
 * séquence `/v4/` et qu'un remplacement aveugle aurait cassé les imports.
 *
 * ── Pourquoi garder l'ALIAS et ne pas simplement supprimer ────────────────
 *
 * Un signet, un lien dans une conversation, un onglet resté ouvert : `/v4/…`
 * doit continuer de mener quelque part. En alias, les deux formes répondent et
 * la barre d'adresse affiche la forme nue. Le jour où plus rien ne pointe vers
 * `/v4`, cette fonction disparaît d'une ligne.
 *
 * ── Pourquoi PAS `createWebHistory('/v4/')` ────────────────────────────────
 *
 * Une base d'historique rend les chemins passés à `push` relatifs à elle :
 * `push('/v4/x')` viserait alors `/v4/v4/x`. Le préfixe doit être dans les
 * ROUTES, pas dans la base.
 */
function withLegacyV4Alias(list: RouteRecordRaw[]): RouteRecordRaw[] {
  return list.map((r) => {
    const path = r.path;
    if (typeof path !== 'string' || !path.startsWith('/')) return r;
    return { ...r, alias: `/v4${path}` } as RouteRecordRaw;
  });
}

const routes: RouteRecordRaw[] = [
  // La racine mène à l'accueil du compte par son NOM, pour atterrir sur la
  // forme canonique plutôt que sur l'alias : le portefeuille, ou le premier
  // écran que ses modules ouvrent (`accueilDuCompte`). Un compte qui n'ouvre
  // rien arrive sur l'écran de refus, qui NOMME ce qui manque, pas sur une
  // page blanche.
  { path: '/', redirect: () => accueilDuCompte() },
  ...withLegacyV4Alias(screens),
  // `adminScreens` porte deja son propre alias `/studio/...` : lui appliquer
  // `withLegacyV4Alias` en plus ecraserait celui-la, et les liens d'hier
  // tomberaient sur le filet 404.
  ...adminScreens,
  {
    path: '/access-denied',
    name: 'access-denied',
    component: () => import('./views/AccessDeniedView.vue'),
  },
  // Le filet. Une route inconnue affichait une page blanche muette : pas
  // d'erreur, pas de requête, indiscernable d'un chargement infini. Elle
  // NOMME désormais le chemin manquant.
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('./views/NotFoundView.vue') },
];

const router = createRouter({ history: createWebHistory(), routes });
// Après un déploiement, un onglet ouvert demande parfois un morceau de page qui
// n'existe plus (son nom change à chaque construction) : il se recharge sur
// la page voulue au lieu de rester blanc (30 septembre 2026).
router.onError((err, vers) => {
  if (/Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(String((err as Error)?.message ?? err))) {
    window.location.assign(vers.fullPath);
  }
});

const session = useSession();

// L'init de session précède la première navigation : sans ça, un rechargement
// sur une route protégée renvoie vers /login alors que la session est valide,
// juste parce qu'elle n'était pas encore lue du stockage.
await session.init();

// ── Ce que l'API accepte de nous, chargé AVANT le premier écran ────────────
//
// Sans elle, les boutons s'affichent une fraction de seconde en « autorisé »
// avant de se griser — et un écran qui se réorganise sous la main se lit comme
// un bug. L'attente est un seul appel, servi depuis les claims côté API : pas
// d'I/O base.
//
// Un échec n'empêche PAS le démarrage : la table reste vide, tout reste
// cliquable, et c'est l'API qui refuse — le comportement d'avant ce
// dispositif, jamais pire.
if (session.isAuthenticated.value) await loadCapabilities();

/**
 * Les écrans qui exigent une permission de MODULE.
 *
 * ── Le défaut que ceci ferme ────────────────────────────────────────────────
 *
 * `orion-admin` accordait `core:territories` et `core:weights` depuis un
 * moment, et RIEN ici ne les lisait : un compte sans aucune permission ouvrait
 * la configuration globale des territoires — celle qui modifie le référentiel
 * partagé par toutes les organisations.
 *
 * Le garde de navigation n'est pas la sécurité — les claims sont lus sans
 * vérifier la signature, et un utilisateur déterminé les contourne. C'est
 * `requireModuleAccess`, côté API, qui refuse. Ici on évite seulement d'ouvrir
 * un écran dont chaque action rendrait 403, ce qui se lit comme une panne.
 *
 * Déclaré par NOM de route et non par chemin : les chemins ont déjà changé
 * deux fois, et un contrôle qui se périme en silence est pire qu'aucun.
 *
 * Un tableau veut dire : l'UNE des permissions suffit. C'est la notation de
 * `module-map.ts` côté API, reprise telle quelle pour qu'une règle se lise pareil
 * des deux côtés.
 */
type RegleModule = string | readonly string[];

const MODULE_PAR_ROUTE: Record<string, RegleModule> = {
  // Général
  'v4-portfolio': 'portfolio',
  'v4-cosmic-map': 'map',
  // Les conversations naissent dans plusieurs modules : l'un d'eux suffit.
  // Leur propre clé depuis le 6 octobre 2026 (Orion Admin, registre `core`).
  'v4-conversations': ['conversations'],
  // Zeus : l'un des modules où il agit suffit (3 octobre 2026).
  'v4-zeus': ['zeus'],
  'v4-simulation': 'simulation',

  // Dossier : mêmes permissions que leurs écrans d'origine par marque.
  'v4-dossier-bureau': 'decisions',
  'v4-dossier-isos': 'arena',
  'v4-dossier-calendar': 'calendar',
  'v4-dossier-sentinel': 'sentinel',
  'v4-discovery': 'discovery',
  'v4-replica': 'replica',
  'v4-radar': 'radar',
  'v4-catalogue': 'catalogue',
  // Entertainment : un module, tous ses écrans (livrables compris).
  'v4-ent-formats': 'entertainment',
  'v4-ent-format': 'entertainment',
  'v4-ent-format-documents': 'entertainment',
  'v4-ent-format-ecoute': 'entertainment',
  'v4-ent-format-suivi': 'projector-suivi',
  'v4-ent-format-marche': 'entertainment',
  'v4-ent-test': 'entertainment',
  'v4-ent-soirees': 'entertainment',
  'v4-ent-enveloppe': 'entertainment',
  'v4-ent-diffuseurs': 'entertainment',
  'v4-ent-diffuseur': 'entertainment',
  'v4-ent-brief': 'entertainment',
  'v4-ent-pays': 'entertainment',
  'v4-ent-pays-detail': 'entertainment',
  'v4-ent-livrable': 'entertainment',
  'v4-ent-sonar': 'entertainment',
  'v4-ent-sonar-etude': 'entertainment',
  'v4-ent-format-sonar': 'entertainment',
  'v4-ent-format-sonar-etude': 'entertainment',
  // Polis (3 octobre 2026) : chaque onglet a sa clé ; l'une d'elles ouvre l'écran.
  // Son Sonar a sa clé depuis le 11 octobre 2026 : `polis-sonar`.
  'v4-polis': ['polis-projection', 'polis-candidats', 'polis-electorat', 'polis-scenarios', 'polis-sonar', 'polis-registre', 'polis'],
  // L'annuaire des personnalités et leur suivi (15 octobre 2026).
  'v4-polis-personnalites': 'polis-annuaire',
  'v4-polis-personnalite': 'polis-annuaire',
  'v4-polis-suivis': 'polis-suivi',
  // La carte des personnalités (16 octobre 2026) : sa propre clé.
  'v4-polis-carte': 'polis-carte',
  // Le Sonar seul (2 octobre 2026).
  'v4-sonar': 'sonar',
  'v4-sonar-etude': 'sonar',
  'v4-sonar-livrable': 'sonar',
  // PreCog n'a pas d'écran par marque : son origine est l'administration,
  // gardée par `territories`. Au dossier, `precog` l'ouvre aussi, sans donner
  // la clé du référentiel partagé. Même règle que l'entrée de navigation et
  // que les routes d'écriture de l'API.
  'v4-dossier-precog': ['precog', 'territories'],

  // Marque
  'v4-channel': 'channel',
  // Une plongée est un forage DANS la chaîne : pas de module propre au
  // registre, et lui en inventer un ici créerait une permission qu'aucune
  // console ne sait accorder.
  'v4-deep-dive': 'channel',
  'v4-audits': 'audits',
  'v4-audit-report': 'audits',
  'v4-decisions': 'decisions',
  'v4-decision-dossier': 'decisions',
  'v4-brand-arena': 'arena',
  // La présence IA se lit sur la chaîne (et se mesure à l'audit) : même
  // permission que l'entrée de navigation qui y menait.
  'v4-presence-llm': 'channel',
  'v4-monday-brief': 'monday-brief',
  'v4-brand-territories': 'my-territories',
  'v4-studio-calendar': 'calendar',
  'v4-brief': 'brief',
  'v4-blast': 'blast',
  'v4-program': 'program',
  'v4-studio-drifts': 'drifts',

  // Studio
  'v4-studio-wizard': 'wizard',

  // Hors navigation, mais atteignables par URL — donc à contrôler. Les
  // omettre aurait laissé quatre écrans ouverts à tous, ce qui est exactement
  // le défaut qu'on ferme.
  'v4-studio-config': 'config',
  'v4-studio-personas': 'personas',
  'v4-studio-hubs': 'hubs',
  'v4-studio-authors': 'authors',

  // Administration : configuration GLOBALE, aussi gardée côté API. La gestion
  // des territoires, la station et les pondérations sont parties dans Orion
  // Admin le 8 octobre 2026 : leurs anciennes adresses ne montrent plus que
  // l'écran qui le dit, ouvert à tout compte connecté.
  // PreCog et le banc de calibration interrogent et corrigent le référentiel
  // PARTAGÉ : la navigation les range déjà sous `territories`, l'URL tapée à la
  // main les ouvrait à tous.
  'v4-precog': 'territories',
  'v4-calibration': 'territories',
};

router.beforeEach((to) => {
  // `not-found` est accessible connecté OU non : renvoyer au login sur une URL
  // inconnue ferait croire à une déconnexion.
  if (to.name === 'not-found') return true;
  // L'invitation se lit avant d'avoir un compte : c'est elle qui le crée.
  if (to.name === 'invitation') return true;
  if (to.name !== 'login' && !session.isAuthenticated.value) return { name: 'login' };
  if (to.name === 'login' && session.isAuthenticated.value) return accueilDuCompte();

  // `access-denied` est joignable des qu'on est connecté, SANS condition de
  // module. C'est ce qui rend ce garde sûr : renvoyer un refus vers le
  // portefeuille — lui-même un module — bouclerait à l'infini sur un compte
  // qui ne l'a pas, et l'onglet se figerait sans rien dire.
  if (to.name === 'access-denied') return true;

  // La page Équipe n'est pas un module : c'est le rôle d'administrateur.
  if (to.name === 'v4-equipe' && !session.estAdministrateur.value) {
    return { name: 'access-denied', query: { module: 'administrateur de l’organisation' } };
  }

  const requis = typeof to.name === 'string' ? MODULE_PAR_ROUTE[to.name] : undefined;
  if (requis !== undefined) {
    const candidats = typeof requis === 'string' ? [requis] : requis;
    if (!candidats.some((m) => session.canOpen(m))) {
      // TOUTES les permissions qui auraient suffi, comme le message de l'API :
      // n'en nommer qu'une enverrait demander celle-là alors que l'autre ouvre
      // aussi l'écran.
      return { name: 'access-denied', query: { module: candidats.map((m) => `core:${m}`).join(' ou ') } };
    }
  }

  return true;
});

createApp(App).use(router).use(i18n).mount('#app');
